import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { getUserTypes, renewMembership, getProfileMetaData, saveProfileMetaData, refreshToken } from '../api';
import { useAuth } from '../context/AuthContext';
import { useClub } from '../context/ClubContext';
import { TAILWIND_COLORS } from '../components/ColorPicker';
import ClubTermsModal from '../components/ClubTermsModal';
import PaymentModal from '../components/PaymentModal';
import ErrorModal from '../components/ErrorModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { buildRenewReference, buildSignUpReference } from '../utils/paymentReference';
import { interpolateMembershipForm, getMembershipFormVariables } from '../utils/membershipFormTemplate';
import {
  buildMembershipMetaDataPayload,
  getMembershipFormFields,
  getMembershipUserTypeId
} from '../utils/membershipFormData';
import {
  buildApprovalBannerHtml,
  buildMembershipSwitchWarningHtml,
  buildRejectionBannerHtml,
  getCardTags,
  hasActiveMembership,
  hasCompletedApplication,
  isMembershipExpired,
  shouldRefreshMembershipSession,
  STATUS_BADGE_BASE_CLASS,
  userTypeSupportsOnlineSignup
} from '../utils/memberStatus';

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(numericValue);
  } catch {
    return `R${numericValue.toFixed(2)}`;
  }
};

const formatTerm = (term) => {
  const months = Number(term || 0);
  if (!Number.isFinite(months) || months <= 0) return 'Term to be confirmed';
  return `${months} ${months === 1 ? 'month' : 'months'}`;
};

const hasInProgressApplication = (memberStatus) => {
  if (!memberStatus) return false;
  if (memberStatus.application === 'pending') return true;
  if (memberStatus.application === 'missingData') return true;
  if (memberStatus.application === 'rejected') return true;
  if (memberStatus.application === 'approved' && memberStatus.paid !== true) return true;
  return false;
};

const canPayMembershipOnline = (membership) => membership?.membership?.zapperFee !== undefined;

const isExistingMember = (user) => Boolean(user?.userTypeId && String(user.userTypeId) !== '0');

const Memberships = () => {
  const { user, loginUser } = useAuth();
  const { clubDetails, getColourStyle } = useClub();
  const [userTypes, setUserTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successDialog, setSuccessDialog] = useState(null);
  const [initialFormData, setInitialFormData] = useState({});
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [profileMetaData, setProfileMetaData] = useState({});
  const [memberStatus, setMemberStatus] = useState(user?.memberStatus ?? null);
  const [cancelConfirmMembership, setCancelConfirmMembership] = useState(null);
  const [isCancellingApplication, setIsCancellingApplication] = useState(false);

  const refreshApplicationState = useCallback(async (jwtUser = user) => {
    try {
      let response = await getProfileMetaData();

      if (shouldRefreshMembershipSession(response, jwtUser)) {
        const newToken = await refreshToken();
        loginUser(newToken);
        response = await getProfileMetaData();
      }

      setProfileMetaData(response?.metaData || {});
      setMemberStatus(response?.memberStatus ?? null);
      return response;
    } catch (error) {
      console.error('[Memberships] Failed to refresh application state:', error);
      return null;
    }
  }, [user, loginUser]);

  useEffect(() => {
    let isMounted = true;

    const loadMemberships = async () => {
      try {
        setIsLoading(true);
        const types = await getUserTypes();
        if (isMounted) {
          setUserTypes(Array.isArray(types) ? types : []);
        }
        if (isMounted) {
          await refreshApplicationState();
        }
      } catch (error) {
        console.error('[Memberships] Failed to load memberships:', error);
        if (isMounted) {
          setErrorMessage('Unable to load memberships. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMemberships();

    return () => {
      isMounted = false;
    };
  // Load once on mount; refreshApplicationState is also called when opening the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.memberStatus !== undefined) {
      setMemberStatus(user.memberStatus ?? null);
    }
  }, [user?.memberStatus]);

  const applicationUserTypeId = useMemo(
    () => getMembershipUserTypeId(profileMetaData),
    [profileMetaData]
  );

  const memberships = useMemo(() => {
    const currentUserTypeId = user?.userTypeId != null ? String(user.userTypeId) : null;
    const applicationId = applicationUserTypeId ? String(applicationUserTypeId) : null;
    const prioritizeApplication = hasInProgressApplication(memberStatus) && Boolean(applicationId);

    const getSortRank = (membershipId) => {
      const id = String(membershipId);
      if (currentUserTypeId && currentUserTypeId !== '0' && id === currentUserTypeId) return 0;
      if (prioritizeApplication && id === applicationId) return 1;
      return 2;
    };

    return userTypes
      .filter((type) => String(type.id) !== '0')
      .filter((type) => String(type?.membership?.description || '').trim())
      .sort((a, b) => {
        const rankDiff = getSortRank(a.id) - getSortRank(b.id);
        if (rankDiff !== 0) return rankDiff;
        return Number(a.id) - Number(b.id);
      });
  }, [userTypes, user?.userTypeId, applicationUserTypeId, memberStatus]);

  const resolvedMembershipHtml = useMemo(() => {
    if (!selectedMembership?.htmlForm) return '';

    return interpolateMembershipForm(selectedMembership.htmlForm, getMembershipFormVariables(user, {
      userTypeName: selectedMembership.name,
      clubName: clubDetails?.name,
    }));
  }, [selectedMembership, user, clubDetails?.name]);

  const getTotalPaymentAmount = (membership) => {
    const config = membership?.membership || {};
    return (Number(config.fee || 0) || 0) + (Number(config.zapperFee || 0) || 0);
  };

  const openSignupForm = async (membership) => {
    setSelectedMembership(membership);
    setIsLoadingFormData(true);
    setInitialFormData({});

    try {
      const response = await refreshApplicationState();
      setInitialFormData(getMembershipFormFields(response?.metaData));
    } catch (error) {
      console.error('[Memberships] Failed to load saved application data:', error);
      setInitialFormData({});
    } finally {
      setIsLoadingFormData(false);
    }
  };

  const completePayment = async (membership, { renewal = false } = {}) => {
    if (!membership || !user) return;

    const totalPaymentAmount = getTotalPaymentAmount(membership);
    const reference = renewal
      ? buildRenewReference(user.firstName, user.lastName)
      : buildSignUpReference(user.firstName, user.lastName);
    const targetUserTypeId = renewal ? null : String(membership.id);

    if (totalPaymentAmount <= 0) {
      try {
        const response = await renewMembership(reference, targetUserTypeId);
        if (response?.token) {
          loginUser(response.token);
        }
        await refreshApplicationState();
      } catch (error) {
        console.error('[Memberships] Failed to process free membership payment:', error);
        setErrorMessage(`Failed to complete membership payment. Please contact the club with Reference: ${reference}`);
      }
      return;
    }

    setPaymentDetails({
      type: renewal ? 'membership_renewal' : 'membership_signup',
      totalPaymentAmount,
      reference,
      userUuid: user.uuid,
      userDisplayName: `${user.firstName} ${user.lastName}`,
      targetUserTypeId,
      targetUserTypeName: membership.name,
      targetMembership: membership.membership
    });
    setShowPaymentModal(true);
  };

  const isExpiredCurrentMembership = (membership) => (
    String(user?.userTypeId) === String(membership?.id)
    && isMembershipExpired(user?.expiryDate)
  );

  const handleCardAction = (membership) => {
    openSignupForm(membership);
  };

  const handleAcceptSubmit = async (formData) => {
    if (!selectedMembership || !user) return;

    const isCurrentMembership = String(user?.userTypeId) === String(selectedMembership.id);
    const isExpiredRenewal = isExpiredCurrentMembership(selectedMembership)
      && canPayMembershipOnline(selectedMembership);

    if (isCurrentMembership) {
      if (isExpiredRenewal) {
        try {
          await saveProfileMetaData(buildMembershipMetaDataPayload(formData, {
            userTypeId: selectedMembership.id
          }));
        } catch (error) {
          console.error('[Memberships] Failed to save membership data before renewal:', error);
          setErrorMessage('Failed to save your membership details. Please try again.');
          return;
        }

        const membershipToRenew = selectedMembership;
        setSelectedMembership(null);
        await refreshApplicationState();
        completePayment(membershipToRenew, { renewal: true });
        return;
      }

      try {
        await saveProfileMetaData(buildMembershipMetaDataPayload(formData, {
          userTypeId: selectedMembership.id
        }));
        await refreshApplicationState();
        setSelectedMembership(null);
      } catch (error) {
        console.error('[Memberships] Failed to update membership details:', error);
        setErrorMessage('Failed to update your membership details. Please try again.');
      }
      return;
    }

    const isApplicationCard = String(applicationUserTypeId) === String(selectedMembership.id);
    const isApprovedAwaitingPayment = isApplicationCard
      && memberStatus?.application === 'approved'
      && memberStatus?.paid !== true;

    if (isApprovedAwaitingPayment) {
      try {
        await saveProfileMetaData(buildMembershipMetaDataPayload(formData, {
          userTypeId: selectedMembership.id
        }));
      } catch (error) {
        console.error('[Memberships] Failed to save application data before payment:', error);
        setErrorMessage('Failed to save your application. Please try again.');
        return;
      }

      setSelectedMembership(null);
      await refreshApplicationState();

      if (canPayMembershipOnline(selectedMembership)) {
        completePayment(selectedMembership);
      }
      return;
    }

    try {
      await saveProfileMetaData(buildMembershipMetaDataPayload(formData, {
        userTypeId: selectedMembership.id
      }));
    } catch (error) {
      console.error('[Memberships] Failed to save application data:', error);
      setErrorMessage('Failed to save your application. Please try again.');
      return;
    }

    await refreshApplicationState();

    if (selectedMembership.membership?.requiresApproval === true) {
      const membershipName = selectedMembership.name;
      setSelectedMembership(null);
      setSuccessDialog({
        title: "We've received your application",
        message: `Thank you for applying for ${membershipName}. The club will review your application and let you know when it has been approved.`
      });
      return;
    }

    if (!canPayMembershipOnline(selectedMembership)) {
      const membershipName = selectedMembership.name;
      setSelectedMembership(null);
      setSuccessDialog({
        title: 'Your application has been approved',
        message: `Thank you for applying for ${membershipName}. Please contact the club to arrange payment.`
      });
      return;
    }

    setSelectedMembership(null);

    const totalPaymentAmount = getTotalPaymentAmount(selectedMembership);
    const reference = buildSignUpReference(user.firstName, user.lastName);

    const nextPaymentDetails = {
      type: 'membership_signup',
      totalPaymentAmount,
      reference,
      userUuid: user.uuid,
      userDisplayName: `${user.firstName} ${user.lastName}`,
      targetUserTypeId: String(selectedMembership.id),
      targetUserTypeName: selectedMembership.name,
      targetMembership: selectedMembership.membership
    };

    if (totalPaymentAmount <= 0) {
      try {
        const response = await renewMembership(reference, String(selectedMembership.id));
        if (response?.token) {
          loginUser(response.token);
        }
        await refreshApplicationState();
      } catch (error) {
        console.error('[Memberships] Failed to process free membership signup:', error);
        setErrorMessage(`Failed to submit membership signup. Please contact the club with Reference: ${reference}`);
      }
      return;
    }

    setPaymentDetails(nextPaymentDetails);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async () => {
    try {
      const response = await renewMembership(
        paymentDetails.reference,
        paymentDetails.targetUserTypeId || null
      );
      if (response?.token) {
        loginUser(response.token);
      }
      await refreshApplicationState();
    } catch (error) {
      console.error('[Memberships] Error completing membership signup:', error);
      setShowPaymentModal(false);
      setErrorMessage(`Failed to complete membership signup. Please contact the club with Reference: ${paymentDetails.reference}`);
    }
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setPaymentDetails(null);
  };

  const handleCancelApplication = async () => {
    if (!cancelConfirmMembership) return;

    setIsCancellingApplication(true);
    try {
      await saveProfileMetaData(undefined, undefined, { cancelApplication: true });
      setProfileMetaData({});
      setMemberStatus(null);
      setCancelConfirmMembership(null);
      setSelectedMembership(null);
    } catch (error) {
      console.error('[Memberships] Failed to cancel application:', error);
      setErrorMessage('Failed to cancel your application. Please try again.');
      setCancelConfirmMembership(null);
    } finally {
      setIsCancellingApplication(false);
    }
  };

  const getCardButtonLabel = (membership) => {
    const isCurrentMembership = String(user?.userTypeId) === String(membership.id);
    const canSignUpOnline = membership?.membership?.canSignUpOnline === true;
    const isApplicationCard = String(applicationUserTypeId) === String(membership.id);

    if (isCurrentMembership) {
      if (isExpiredCurrentMembership(membership) && canPayMembershipOnline(membership)) {
        return 'Renew Membership';
      }
      return 'View Your Membership';
    }

    if (isApplicationCard && hasInProgressApplication(memberStatus)) {
      return 'View Application';
    }

    if (!canSignUpOnline) {
      return 'Online signup unavailable';
    }

    if (isExistingMember(user)) {
      return 'View Membership';
    }

    return 'Sign Up for Membership';
  };

  const isCardActionDisabled = (membership) => {
    const isCurrentMembership = String(user?.userTypeId) === String(membership.id);
    const canSignUpOnline = membership?.membership?.canSignUpOnline === true;
    const isApplicationCard = String(applicationUserTypeId) === String(membership.id);

    if (isCurrentMembership) return false;
    if (isApplicationCard && hasInProgressApplication(memberStatus)) return false;
    return !canSignUpOnline;
  };

  const getModalShowAccept = () => {
    if (!selectedMembership) return false;

    if (String(user?.userTypeId) === String(selectedMembership.id)) return true;

    const isApplicationCard = String(applicationUserTypeId) === String(selectedMembership.id);

    if (isApplicationCard && memberStatus?.application === 'approved' && memberStatus?.paid !== true) {
      return true;
    }

    if (isApplicationCard && memberStatus?.application === 'pending') return true;
    if (isApplicationCard && memberStatus?.application === 'missingData') return true;
    if (isApplicationCard && memberStatus?.application === 'rejected') return true;

    return selectedMembership?.membership?.canSignUpOnline === true;
  };

  const getModalAcceptLabel = () => {
    if (!selectedMembership) return 'Accept & Submit';

    if (String(user?.userTypeId) === String(selectedMembership.id)) {
      if (isExpiredCurrentMembership(selectedMembership) && canPayMembershipOnline(selectedMembership)) {
        const totalPaymentAmount = getTotalPaymentAmount(selectedMembership);
        return totalPaymentAmount > 0
          ? `Pay ${formatCurrency(totalPaymentAmount)}`
          : 'Renew Membership';
      }
      return 'Update Details';
    }

    const isApplicationCard = String(applicationUserTypeId) === String(selectedMembership.id);

    if (isApplicationCard && memberStatus?.application === 'approved' && memberStatus?.paid !== true) {
      if (canPayMembershipOnline(selectedMembership)) {
        return `Pay ${formatCurrency(getTotalPaymentAmount(selectedMembership))}`;
      }
      return 'Save Application';
    }

    if (isApplicationCard && memberStatus?.application === 'rejected') {
      return 'Resubmit Application';
    }

    if (isApplicationCard && hasInProgressApplication(memberStatus)) {
      return 'Save Application';
    }

    return 'Accept & Submit';
  };

  const getModalLeftFooterActions = () => {
    if (!selectedMembership) return [];

    const isApplicationCard = String(applicationUserTypeId) === String(selectedMembership.id);
    if (!isApplicationCard || !hasInProgressApplication(memberStatus)) return [];

    if (memberStatus?.application !== 'pending'
      && memberStatus?.application !== 'rejected'
      && !(memberStatus?.application === 'approved' && memberStatus?.paid !== true)) {
      return [];
    }

    return [{
      key: 'cancel-application',
      label: 'Cancel Application',
      mobileLabel: 'Cancel',
      variant: 'destructive',
      onClick: () => setCancelConfirmMembership(selectedMembership)
    }];
  };

  const applicationFormPrependHtml = useMemo(() => {
    if (!selectedMembership) return '';

    const isApplicationCard = String(applicationUserTypeId) === String(selectedMembership.id);
    const isCurrentMembership = String(user?.userTypeId) === String(selectedMembership.id);
    const parts = [];

    if (isApplicationCard && memberStatus) {
      if (memberStatus.application === 'rejected' && memberStatus.rejectionReason) {
        parts.push(buildRejectionBannerHtml(memberStatus.rejectionReason, { audience: 'member' }));
      }

      if (memberStatus.application === 'approved' && !hasCompletedApplication(memberStatus)) {
        const canPayOnline = canPayMembershipOnline(selectedMembership);
        parts.push(buildApprovalBannerHtml({
          requiresPayment: canPayOnline && getTotalPaymentAmount(selectedMembership) > 0,
          requiresContactClub: !canPayOnline
        }));
      }
    }

    const shouldShowMembershipSwitchWarning = hasActiveMembership(user)
      && !isCurrentMembership
      && !hasInProgressApplication(memberStatus);

    if (shouldShowMembershipSwitchWarning) {
      parts.push(buildMembershipSwitchWarningHtml(user.userType || 'your current membership', {
        requiresApproval: selectedMembership?.membership?.requiresApproval === true
      }));
    }

    return parts.join('');
  }, [selectedMembership, applicationUserTypeId, memberStatus, user]);

  const buildPaymentText = () => {
    const membership = paymentDetails?.targetMembership || {};
    return `
      <ul class="list-disc pl-5">
        <li>Do not close this window until payment completes</li>
        <li>Membership: <span class="font-bold">${paymentDetails?.targetUserTypeName || ''}</span></li>
        <li>Term: <span class="font-bold">${formatTerm(membership.term)}</span></li>
        <li>Reference: <span class="font-bold break-all">${paymentDetails?.reference || ''}</span></li>
      </ul>
    `;
  };

  const modalAcceptLabel = getModalAcceptLabel();
  const modalAcceptMobileLabel = modalAcceptLabel === 'Save Application' ? 'Save' : '';

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {isLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <svg className="mx-auto h-8 w-8 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-3 text-sm text-slate-600">Loading memberships...</p>
          </div>
        ) : memberships.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800">No memberships available</h3>
            <p className="mt-2 text-sm text-slate-600">
              Membership signup options will appear here once the club publishes them.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {memberships.map((membership) => {
              const color = TAILWIND_COLORS[membership?.membership?.colour] || TAILWIND_COLORS.slate;
              const isCurrentMembership = String(user?.userTypeId) === String(membership.id);
              const isApplicationCard = String(applicationUserTypeId) === String(membership.id);
              const totalPaymentAmount = getTotalPaymentAmount(membership);
              const cardTags = (() => {
                // Application status tags only on the membership type stored in metaData
                if (isApplicationCard && memberStatus) {
                  const isExpired = isCurrentMembership && isMembershipExpired(user?.expiryDate);
                  return getCardTags(memberStatus, { isExpired });
                }

                if (isCurrentMembership && !isApplicationCard) {
                  const isExpired = isMembershipExpired(user?.expiryDate);

                  if (isExpired) {
                    return getCardTags({ paid: true, application: 'approved' }, { isExpired: true });
                  }

                  if (userTypeSupportsOnlineSignup(membership) && !memberStatus) {
                    return getCardTags({ application: 'missingData' });
                  }

                  return [];
                }

                return [];
              })();

              return (
                <article
                  key={membership.id}
                  className={`relative flex min-h-[280px] flex-col overflow-hidden rounded-2xl border bg-white shadow-md transition-shadow hover:shadow-lg ${color.border}`}
                >
                  <div className={`h-2 ${color.bg}`} />
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-xl font-semibold text-slate-800">
                      {membership.name}
                    </h3>
                    {cardTags.length > 0 && (
                      <div className="mt-2 mb-4 flex flex-wrap gap-1.5">
                        {cardTags.map((tag) => (
                          <span
                            key={tag.key}
                            className={`${STATUS_BADGE_BASE_CLASS} ${tag.badgeClass}`}
                          >
                            {tag.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {!cardTags.length && <div className="mb-4" />}

                    <p className="text-sm leading-6 text-slate-600">
                      {membership.membership.description}
                    </p>

                    <div className="mt-auto pt-5">
                      <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Term</p>
                          <p className="mt-1 text-sm font-semibold text-slate-800">{formatTerm(membership.membership.term)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Fee</p>
                          <p className="mt-1 text-sm font-semibold text-slate-800">{formatCurrency(totalPaymentAmount)}</p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleCardAction(membership)}
                        disabled={isCardActionDisabled(membership)}
                        className={`mt-4 w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100 ${
                          isCurrentMembership
                            ? `border ${getColourStyle(membership?.membership?.colour)}`
                            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                        }`}
                      >
                        {getCardButtonLabel(membership)}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ClubTermsModal
        isOpen={!!selectedMembership && !isLoadingFormData}
        onClose={() => setSelectedMembership(null)}
        htmlContent={resolvedMembershipHtml}
        initialFormData={initialFormData}
        showAccept={getModalShowAccept()}
        acceptLabel={modalAcceptLabel}
        acceptMobileLabel={modalAcceptMobileLabel}
        prependHtml={applicationFormPrependHtml}
        leftFooterActions={getModalLeftFooterActions()}
        onAccept={handleAcceptSubmit}
        membershipTypeName={selectedMembership?.name}
        membershipColour={selectedMembership?.membership?.colour}
      />

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        totalPaymentAmount={paymentDetails?.totalPaymentAmount || 0}
        reference={paymentDetails?.reference || ''}
        onPaymentComplete={handlePaymentComplete}
        paymentDetails={paymentDetails}
        customText={buildPaymentText()}
        showTimer={false}
      />

      <ConfirmationModal
        isOpen={!!cancelConfirmMembership}
        onClose={() => {
          if (!isCancellingApplication) setCancelConfirmMembership(null);
        }}
        onConfirm={handleCancelApplication}
        title="Cancel Application"
        message={`Are you sure you want to cancel your application for ${cancelConfirmMembership?.name || 'this membership'}? This will remove your saved application data.`}
        confirmText="Cancel Application"
        isDestructive
        isLoading={isCancellingApplication}
      />

      <Dialog open={!!successDialog} onClose={() => setSuccessDialog(null)} className="fixed z-50 inset-0 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          <Dialog.Panel className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-4">
            <Dialog.Title className="text-xl font-semibold text-slate-800 mb-4">
              {successDialog?.title}
            </Dialog.Title>
            <div className="mb-4 text-slate-600 leading-relaxed">
              {successDialog?.message}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setSuccessDialog(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage('')}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default Memberships;
